/** The place a file goes in: the drop target, the two choices that go with it, and what the last send
 * answered - ADR-0212's Upload place. Everything the old Evidence panel asked and refused is here
 * under the same test ids; what changed is that the sentences moved behind info controls.
 *
 * **A drop target, and not the only way in.** The target is a `<label>` for the real
 * `<input type="file">`, so a keyboard and a screen reader reach the control and the label *is* its
 * name; a drop takes **every** file and sends them in turn (`useSendFiles`). A drop is ignored until
 * the role is known to be allowed, for the reason the input is disabled until then.
 *
 * **The role decides whether there is a picker, not whether it works** (F-005, design rule 6): a
 * Viewer or an Auditor is told, once, that their role does not upload, and there is no control that
 * would collect a 403.
 *
 * **The limit is said whichever way the read went** (design principle 5): loading says nothing for a
 * moment, a failed read says it could not be read, and it is never an absent sentence a reader would
 * take for "no limit".
 */

import { CheckCircle, FileArrowUp } from "@phosphor-icons/react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import { ReadFailed, WriteFailed } from "../../components/Loaded";
import { InfoPopover, StatusChip } from "../../components/ui";
import { KIND_LABEL } from "../../lib/documentKinds";
import { fileSize } from "../../lib/format";
import { ACCEPT_ATTRIBUTE, UPLOAD_TYPES } from "../../lib/uploadTypes";
import { keepSearch } from "../search";
import type { ScreenSearch } from "../search";
import type { Screen } from "../useScreen";
import styles from "./DropZone.module.css";
import type { useSendFiles } from "./useSendFiles";

type Sender = ReturnType<typeof useSendFiles>;

export function DropZone({ sender, method }: { sender: Sender; method: Screen["method"] }) {
  const navigate = useNavigate();
  const [dragging, setDragging] = useState(false);
  const {
    mayUpload,
    realDocumentsPermitted,
    limit,
    siteList,
    kind,
    setKind,
    siteHint,
    setSiteHint,
    message,
    refusal,
    setRefusal,
    oversize,
    duplicate,
    progress,
    interrupted,
    received,
    sending,
    onFiles,
    onUpload,
  } = sender;

  return (
    <section className={`card ${styles.panel}`} aria-label="Send files" data-testid="upload-panel">
      {realDocumentsPermitted ? null : (
        <span className={styles.notice} data-tone="warn" data-testid="upload-synthetic-only">
          Sample documents only
          <InfoPopover infoKey="upload.sample-only" />
        </span>
      )}
      {mayUpload === false ? (
        <p className={styles.state} data-testid="upload-who-can">
          Your role cannot upload.
          <InfoPopover infoKey="upload.who-can-upload" />
        </p>
      ) : (
        <>
          <div className={styles.context}>
            <label className={styles.field}>
              <span>What is it?</span>
              <select
                aria-label="What kind of document is it?"
                disabled={mayUpload === undefined}
                value={kind}
                onChange={(event) => setKind(event.target.value)}
              >
                {Object.entries(KIND_LABEL).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            {/* Optional, and a hint: it pre-fills the match, and the reviewer still chooses the
                meter. Not offered when the sites could not be read (an empty list there would read as
                "no sites"), and the failure is said, once, in place. */}
            <label className={styles.field}>
              <span>
                Which site? (optional)
              </span>
              <select
                aria-label="Which site is it about?"
                disabled={mayUpload === undefined || siteList.isPending || siteList.isError}
                value={siteHint}
                onChange={(event) => setSiteHint(event.target.value)}
                data-testid="upload-site"
              >
                <option value="">Not stated</option>
                {(siteList.data ?? []).map((site) => (
                  <option key={site.id} value={site.id}>
                    {site.name}
                  </option>
                ))}
              </select>
            </label>
            {siteList.isError ? (
              <ReadFailed
                of="The list of sites"
                error={siteList.error}
                onRetry={() => void siteList.refetch()}
              />
            ) : null}
            <InfoPopover infoKey="upload.site-hint" />
          </div>

          <div
            className={`${styles.dropzone} ${dragging ? styles.over : ""}`}
            data-testid="upload-dropzone"
            data-dragging={String(dragging)}
            onDragOver={(event) => {
              event.preventDefault();
              if (mayUpload === true) setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              if (mayUpload !== true || sending) return;
              const dropped = Array.from(event.dataTransfer.files);
              if (dropped.length > 0) void onFiles(dropped);
            }}
          >
            {/* The label is the control's name and its hit area; the input inside it is the real,
                keyboard-reachable file input that the tests and the browser gate drive. */}
            <label className={styles.target}>
              <FileArrowUp size={34} weight="duotone" aria-hidden="true" className={styles.icon} />
              <span className={styles.title}>Drop bills here, or choose files</span>
              <input
                type="file"
                multiple
                aria-label="File"
                className={styles.pick}
                data-testid="upload-file"
                accept={ACCEPT_ATTRIBUTE}
                disabled={mayUpload === undefined}
                onChange={(event) => {
                  const chosen = Array.from(event.target.files ?? []);
                  event.target.value = "";
                  if (chosen.length > 0) void onFiles(chosen);
                }}
              />
            </label>
            <ul className={styles.types} aria-label="Accepted file types" data-testid="upload-types">
              {UPLOAD_TYPES.map((type) => (
                <li key={type.label}>
                  <StatusChip tone="neutral" size="sm">
                    {type.label}
                  </StatusChip>
                </li>
              ))}
              <li>
                {limit.data ? (
                  <span
                    className={styles.chip}
                    data-tone="neutral"
                    data-testid="upload-limit"
                    data-max-bytes={limit.data.max_bytes}
                  >
                    {/* The server's own fragment ("the limit is 4 MB"), capitalised and not parsed, so
                        a server that rewords it cannot leave this screen printing half a sentence. */}
                    {`${limit.data.message.charAt(0).toUpperCase()}${limit.data.message.slice(1)}`}
                  </span>
                ) : limit.isError ? (
                  <span
                    className={styles.chip}
                    data-tone="warn"
                    role="alert"
                    data-testid="upload-limit-unread"
                  >
                    Size limit unread
                    {/* A failure with no way out is a dead end (G-P9-17): asking again is the remedy. */}
                    <button
                      type="button"
                      className={styles.chipRetry}
                      onClick={() => void limit.refetch()}
                      aria-label="Try again: read the file-size limit"
                    >
                      Try again
                    </button>
                  </span>
                ) : (
                  // Still asking: a grey chip, not the absence of one (the limit is read once a
                  // screen opens, and a missing chip reads as "no limit").
                  <span
                    className={`skeleton ${styles.chipBone}`}
                    role="status"
                    aria-busy="true"
                    data-testid="upload-limit-loading"
                  >
                    <span className="visuallyHidden">Loading…</span>
                  </span>
                )}
              </li>
              <li>
                <InfoPopover infoKey="upload.file-limits" />
              </li>
            </ul>
          </div>

          {/* The browser's own refusal. `role="alert"` because it is the answer to something the
              reader just did, and nothing else on the screen changes when a file is turned away. */}
          {oversize ? (
            <p className={styles.error} role="alert" data-testid="upload-too-large">
              {oversize}
            </p>
          ) : null}

          {/* **Said while it happens** (ADR-0058): three requests and a large body between them, and
              a screen that does nothing for forty seconds is indistinguishable from one that has
              failed. Structural attributes, not sentences - `data-step` is what a test or a gate
              asserts. */}
          {progress ? (
            <div className={styles.progress} role="status" data-testid="upload-progress" data-step={progress.step}>
              <progress
                aria-label="Sending the file"
                max={Math.max(progress.total, 1)}
                value={progress.step === "declaring" ? 0 : progress.sent}
              />
              <span>
                {progress.step === "declaring"
                  ? "Getting ready to send."
                  : progress.step === "sending"
                    ? `Sending ${fileSize(progress.sent)} of ${fileSize(progress.total)}.`
                    : "Checking what arrived."}
              </span>
            </div>
          ) : null}

          {/* **What happened, and what is next** (Q-07, G-Wcontrib-01). A file the server took used to
              change one tile and add a row far down the page. Reading it is a person's act (the
              redaction gate is on it) and it is done on another screen, so the sentence names the
              step and links to the place. A status chip's text is not body copy (`data-tone`), and
              this is said only of files the server took: a refusal has its own alert below. */}
          {received && !sending ? (
            <p
              className={styles.received}
              role="status"
              data-tone="ok"
              data-testid="upload-received"
              data-count={received.count}
            >
              <CheckCircle size={16} weight="fill" aria-hidden="true" />
              <span>
                {received.count === 1 ? `Received ${received.lastName}.` : `Received ${received.count} files.`}{" "}
                Next: {received.tabular === received.count ? "review the import" : "read it"} on{" "}
                <Link
                  to="/processing"
                  search={(prev: Partial<ScreenSearch>) => ({ ...keepSearch(prev), tab: "in-progress" })}
                >
                  Processing &gt; In progress
                </Link>
                .
              </span>
            </p>
          ) : null}

          {/* **An interruption, with the retry that fits it.** `data-stage` is the branch: after a
              failed send nothing was kept and "Send again" starts over; after a failed confirmation the
              bytes may be there and "Confirm again" asks the server the same question, which it
              answers rather than repeats, so it cannot add the document twice. */}
          {interrupted ? (
            <div className={styles.alert} role="alert" data-testid="upload-interrupted" data-stage={interrupted.error.stage}>
              <p>{interrupted.error.message}</p>
              <button
                className={styles.action}
                type="button"
                onClick={() =>
                  void onUpload(
                    interrupted.file,
                    interrupted.allowDuplicate,
                    interrupted.error.stage === "confirm" ? interrupted.error.intentId : undefined,
                  )
                }
              >
                {interrupted.error.stage === "confirm" ? "Confirm again" : "Send again"}
              </button>
            </div>
          ) : null}

          {duplicate ? (
            <div className={styles.alert} data-testid="upload-duplicate">
              <p>EmitIQ already holds this exact file.</p>
              <button
                className={styles.action}
                type="button"
                onClick={() =>
                  void navigate({
                    to: "/evidence/$documentId",
                    params: { documentId: duplicate.existingId },
                    search: { method },
                  })
                }
              >
                Show what is already held
              </button>
              <button className={styles.action} type="button" onClick={() => void onUpload(duplicate.file, true)}>
                Add it anyway
              </button>
            </div>
          ) : null}

          {message ? (
            <p className={styles.error} role="alert">
              {message}
            </p>
          ) : null}
          {/* **No "Try again".** Every ordinary refusal here is about the file - a scan, a kind
              EmitIQ does not read, a mismatched content type - and its remedy is to choose a
              different one, which the picker above already is. A retry button would re-send the same
              bytes and fail identically. */}
          <WriteFailed error={refusal} of="the upload" onReload={() => setRefusal(null)} />
        </>
      )}
    </section>
  );
}
